/**
 * Parameter libraries API endpoints — T20.
 * Maps PRD §9.6 to typed fetch calls via apiClient.
 */
import {
  type CreateLibraryEntryRequest,
  type LibraryEntryResponse,
  LibraryEntryResponseSchema,
  ListLibraryEntriesResponseSchema,
  type ParameterLibraryType,
  type UpdateLibraryEntryRequest,
} from '@malaberg/shared';
import { apiClient } from '../client';

export interface ListLibraryQuery {
  scope?: string;
  status?: 'active' | 'archived';
  limit?: number;
  offset?: number;
}

function toQueryString(params: Record<string, string | number | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

type LibraryEntryEnvelope = { ok: boolean; entry: LibraryEntryResponse };

function isLibraryEntryResponse(value: unknown): value is LibraryEntryResponse {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { id?: unknown }).id === 'string' &&
    typeof (value as { displayName?: unknown }).displayName === 'string'
  );
}

export function unwrapLibraryEntryResponse(
  response: LibraryEntryResponse | LibraryEntryEnvelope
): LibraryEntryResponse {
  if (
    typeof response === 'object' &&
    response !== null &&
    'entry' in response &&
    isLibraryEntryResponse((response as { entry?: unknown }).entry)
  ) {
    return (response as LibraryEntryEnvelope).entry;
  }

  if (isLibraryEntryResponse(response)) {
    return response;
  }

  throw new Error('Invalid library entry response');
}

export const librariesApi = {
  list(type: ParameterLibraryType, query: ListLibraryQuery = {}) {
    return apiClient.request(
      'GET',
      `/api/libraries/${type}${toQueryString(query as Record<string, string | number | undefined>)}`,
      {
        schema: ListLibraryEntriesResponseSchema,
      }
    );
  },

  getVersions(type: ParameterLibraryType, id: string) {
    return apiClient.request('GET', `/api/libraries/${type}/${id}/versions`);
  },

  getVersion(type: ParameterLibraryType, id: string, version: number) {
    return apiClient.request('GET', `/api/libraries/${type}/${id}/versions/${version}`, {
      schema: LibraryEntryResponseSchema,
    });
  },

  async create(type: ParameterLibraryType, body: Omit<CreateLibraryEntryRequest, 'type'>) {
    const response = await apiClient.request<LibraryEntryEnvelope | LibraryEntryResponse>(
      'POST',
      `/api/libraries/${type}`,
      {
        body: JSON.stringify({ ...body, type }),
      }
    );
    return unwrapLibraryEntryResponse(response);
  },

  update(type: ParameterLibraryType, id: string, body: UpdateLibraryEntryRequest) {
    return apiClient.request('PUT', `/api/libraries/${type}/${id}`, {
      body: JSON.stringify(body),
    });
  },

  revert(type: ParameterLibraryType, id: string, version: number) {
    return apiClient.request('POST', `/api/libraries/${type}/${id}/revert/${version}`, {
      body: JSON.stringify({}),
    });
  },

  delete(type: ParameterLibraryType, id: string) {
    return apiClient.request('DELETE', `/api/libraries/${type}/${id}`);
  },
};
